import React, { useState, useEffect } from 'react';
import { TrainingApi, TrainingProgress } from '@/features/agents';
import { Play, CheckCircle, Clock, BookOpen } from 'lucide-react';
import { AuthService } from '@/features/auth';

export const TrainingDashboard: React.FC = () => {
  const [trainingProgress, setTrainingProgress] = useState<TrainingProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedModule, setSelectedModule] = useState<TrainingProgress | null>(null);
  const [showVideo, setShowVideo] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string>('');

  useEffect(() => {
    // Check authentication status
    const isAuth = AuthService.isAuthenticated();
    const token = AuthService.getToken();
    
    loadTrainingProgress();
  }, []);

  const loadTrainingProgress = async () => {
    try {
      setLoading(true);
      
      // Check if user is authenticated
      if (!AuthService.isAuthenticated()) {
        console.error('User not authenticated');
        alert('Please log in to access training modules.');
        return;
      }
      
      const progress = await TrainingApi.getUserProgress();
      setTrainingProgress(progress);
    } catch (error) {
      console.error('Error loading training progress:', error);
      alert('Failed to load training progress. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkCompleted = async (moduleId: string) => {
    try {
      
      // Check if user is authenticated
      if (!AuthService.isAuthenticated()) {
        console.error('User not authenticated');
        alert('Please log in to mark modules as completed.');
        return;
      }
      
      const result = await TrainingApi.markModuleCompleted(moduleId);
      await loadTrainingProgress(); // Refresh the list
    } catch (error) {
      console.error('Error marking module completed:', error);
      alert('Failed to mark module as completed. Please try again.');
    }
  };

  const handleWatchVideo = async (moduleId: string) => {
    try {
      const module = await TrainingApi.getModuleById(moduleId);
      if (module && module.videoUrl) {
        setVideoUrl(module.videoUrl);
        setShowVideo(true);
      }
    } catch (error) {
      console.error('Error loading video:', error);
    }
  };

  const getModuleTypeIcon = (moduleType: string) => {
    switch (moduleType) {
      case 'SALES_REPORTS':
        return <BookOpen className="h-5 w-5 text-blue-600" />;
      case 'COMPLIANCE':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'ENROLLMENT':
        return <Play className="h-5 w-5 text-purple-600" />;
      case 'SELF_ENROLLMENT':
        return <Play className="h-5 w-5 text-orange-600" />;
      default:
        return <BookOpen className="h-5 w-5 text-gray-600" />;
    }
  };

  const getModuleTypeColor = (moduleType: string) => {
    switch (moduleType) {
      case 'SALES_REPORTS':
        return 'bg-blue-100 text-blue-800';
      case 'COMPLIANCE':
        return 'bg-green-100 text-green-800';
      case 'ENROLLMENT':
        return 'bg-purple-100 text-purple-800';
      case 'SELF_ENROLLMENT':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getModuleTypeName = (moduleType: string) => {
    switch (moduleType) {
      case 'SALES_REPORTS':
        return 'Sales Reports';
      case 'COMPLIANCE':
        return 'Compliance';
      case 'ENROLLMENT':
        return 'Enrollment';
      case 'SELF_ENROLLMENT':
        return 'Self-Enrollment';
      default:
        return 'General';
    }
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="h-6 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-48 bg-[length:200%_100%] animate-[shimmer_2s_infinite]"></div>
          <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-64 mt-2 bg-[length:200%_100%] animate-[shimmer_2s_infinite]"></div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {Array.from({ length: 5 }).map((_, index) => (
                  <th key={index} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-20 bg-[length:200%_100%] animate-[shimmer_2s_infinite]"></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Array.from({ length: 5 }).map((_, index) => (
                <tr key={index} className="animate-pulse">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-5 h-5 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded bg-[length:200%_100%] animate-[shimmer_2s_infinite]"></div>
                      <div className="ml-3 space-y-2">
                        <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-32 bg-[length:200%_100%] animate-[shimmer_2s_infinite]"></div>
                        <div className="h-3 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-16 bg-[length:200%_100%] animate-[shimmer_2s_infinite]"></div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-6 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-full w-20 bg-[length:200%_100%] animate-[shimmer_2s_infinite]"></div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-12 bg-[length:200%_100%] animate-[shimmer_2s_infinite]"></div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-6 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-full w-16 bg-[length:200%_100%] animate-[shimmer_2s_infinite]"></div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-2">
                      <div className="h-8 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-20 bg-[length:200%_100%] animate-[shimmer_2s_infinite]"></div>
                      <div className="h-8 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-24 bg-[length:200%_100%] animate-[shimmer_2s_infinite]"></div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-lg rounded-xl border border-gray-100 overflow-hidden">
      {/* Enhanced Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <BookOpen className="h-7 w-7 text-blue-600 mr-3" />
              Training Modules
            </h2>
            <p className="text-gray-600 mt-2 text-base">
              Complete required training modules to access features and enhance your skills
            </p>
          </div>
          <div className="hidden sm:block">
            <div className="bg-white rounded-lg px-4 py-3 shadow-sm border border-gray-200">
              <div className="text-sm text-gray-500">Progress</div>
              <div className="text-2xl font-bold text-blue-600">
                {trainingProgress.filter(m => m.isCompleted).length}/{trainingProgress.length}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Training Module
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Category
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Duration
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {trainingProgress.map((module, index) => (
              <tr key={module.moduleId} className={`hover:bg-gray-50 transition-colors duration-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`}>
                <td className="px-6 py-5 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center shadow-sm">
                        {getModuleTypeIcon(module.moduleType)}
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-base font-semibold text-gray-900">{module.title}</div>
                      <div className="flex items-center mt-1">
                        {module.isRequired && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 mr-2">
                            <span className="w-1.5 h-1.5 bg-red-500 rounded-full mr-1"></span>
                            Required
                          </span>
                        )}
                        <span className="text-sm text-gray-500">Module ID: {module.moduleId}</span>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-5 whitespace-nowrap">
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${getModuleTypeColor(module.moduleType)} shadow-sm`}>
                    {getModuleTypeName(module.moduleType)}
                  </span>
                </td>
                <td className="px-6 py-5 whitespace-nowrap">
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm font-medium text-gray-900">
                      {formatDuration(module.timeSpent)}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-5 whitespace-nowrap">
                  {module.isCompleted ? (
                    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-green-100 text-green-800 shadow-sm">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Completed
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 shadow-sm">
                      <Clock className="h-4 w-4 mr-2" />
                      Pending
                    </span>
                  )}
                </td>
                <td className="px-6 py-5 whitespace-nowrap text-right">
                  <div className="flex items-center justify-end space-x-3">
                    {!module.isCompleted ? (
                      <>
                        <button
                          onClick={() => handleWatchVideo(module.moduleId)}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-sm"
                        >
                          <Play className="h-4 w-4 mr-2" />
                          Watch Video
                        </button>
                        <button
                          onClick={() => handleMarkCompleted(module.moduleId)}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 shadow-sm"
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Mark Complete
                        </button>
                      </>
                    ) : (
                      <div className="flex items-center text-green-600">
                        <CheckCircle className="h-5 w-5 mr-2" />
                        <span className="text-sm font-medium">Completed</span>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {trainingProgress.length === 0 && (
        <div className="text-center py-16 bg-gray-50">
          <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
            <BookOpen className="h-12 w-12 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No training modules available</h3>
          <p className="text-gray-500 max-w-md mx-auto">
            Training modules will appear here once they are added by administrators. Check back later for new content.
          </p>
        </div>
      )}

      {/* Enhanced Video Modal */}
      {showVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                    <Play className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {selectedModule?.title || 'Training Video'}
                    </h3>
                    <p className="text-sm text-gray-600">Complete this module to advance your training</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowVideo(false);
                    setVideoUrl('');
                    setSelectedModule(null);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Video Content */}
            <div className="p-6">
              <div className="aspect-video bg-gray-100 rounded-xl overflow-hidden shadow-lg">
                <iframe
                  src={videoUrl}
                  title="Training Video"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  {selectedModule && (
                    <span>Module: {selectedModule.moduleType} • Duration: {formatDuration(selectedModule.timeSpent)}</span>
                  )}
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setShowVideo(false);
                      setVideoUrl('');
                      setSelectedModule(null);
                    }}
                    className="px-6 py-2 text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-white transition-colors font-medium"
                  >
                    Close
                  </button>
                  {selectedModule && !selectedModule.isCompleted && (
                    <button
                      onClick={() => {
                        handleMarkCompleted(selectedModule.moduleId);
                        setShowVideo(false);
                        setVideoUrl('');
                        setSelectedModule(null);
                      }}
                      className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium shadow-sm"
                    >
                      <CheckCircle className="h-4 w-4 inline mr-2" />
                      Mark as Complete
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
